import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useArticles } from '../contexts/ArticleContext';
import { visitedCountries, getCountryByTagName } from '../data/countries';
import ArticleCard from '../components/molecules/ArticleCard';
import SEO from '../components/SEO';
import { Article } from '../types';
import { Container, SectionTitle } from '../styles/commonStyles';

const PageWrapper = styled.div`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  min-height: 100vh;
  padding-bottom: ${theme.space.xl};
  position: relative;
  overflow-x: hidden;
  width: 100%;
`;

const CountryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.space.xl} ${theme.space.md};
`;

const CountryHeader = styled.div`
  margin-bottom: ${theme.space.xl};
  text-align: center;
  margin-top: ${theme.space.xl};
`;

const CountryTitleWrapper = styled.div`
  margin-top: ${theme.space.xl};
  margin-bottom: ${theme.space.xl};
`;

const ArticlesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.md};
`;

const NoArticles = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.white};
  padding: ${theme.space.xl} 0;
`;

const HomeButton = styled(Link)`
  background-color: #D67616;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  display: block;
  margin: 30px auto 0;
  text-align: center;
  width: fit-content;
  
  &:hover {
    background-color: #e55a00;
  }
`;

// カスタムの白いパンくずリスト
const WhiteBreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-size: 0.9rem;
`;

const WhiteBreadcrumbItem = styled.div`
  display: flex;
  align-items: center;
  color: white;
  
  &:not(:last-child)::after {
    content: '>';
    margin: 0 8px;
    color: white;
  }
`;

const WhiteBreadcrumbLink = styled(Link)`
  color: white;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
    color: #f0f0f0;
  }
`;

const WhiteBreadcrumbCurrent = styled.span`
  color: white;
  font-weight: 500;
`;

interface BreadcrumbItem {
  label: string;
  path: string;
}

const WhiteBreadcrumb: React.FC<{items: BreadcrumbItem[]}> = ({ items }) => {
  return (
    <WhiteBreadcrumbContainer>
      {items.map((item, index) => (
        <WhiteBreadcrumbItem key={index}>
          {index === items.length - 1 ? (
            <WhiteBreadcrumbCurrent>{item.label}</WhiteBreadcrumbCurrent>
          ) : (
            <WhiteBreadcrumbLink to={item.path}>{item.label}</WhiteBreadcrumbLink>
          )}
        </WhiteBreadcrumbItem>
      ))}
    </WhiteBreadcrumbContainer>
  );
};

const CountryArticlesPage: React.FC = () => {
  const { articles, loading, error } = useArticles();
  const { countryName } = useParams<{ countryName: string }>();
  const decodedCountryName = countryName ? decodeURIComponent(countryName) : '';
  
  // コンポーネントマウント時にスクロール位置をトップに戻す
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // 該当する国のタグを持つ記事をフィルタリング
  const filteredArticles = articles.filter((article: Article) => 
    article.tags && article.tags.includes(decodedCountryName)
  );

  // 国の情報を取得
  const countryInfo = getCountryByTagName(decodedCountryName);

  if (loading) {
    return (
      <PageWrapper>
        <CountryContainer>
          <p>読み込み中...</p>
        </CountryContainer>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <CountryContainer>
          <p>エラーが発生しました: {error}</p>
        </CountryContainer>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO 
        title={`${decodedCountryName}の旅行記事`}
        description={`アラヨットラベル - ${decodedCountryName}の旅行記事一覧です。${decodedCountryName}に関する旅行情報、観光スポット、グルメ情報を提供します。`}
        keywords={`${decodedCountryName}, 旅行, 観光, 国, アラヨットラベル`}
        canonical={`/country/${countryName}`}
      />
      
      <CountryContainer>
        <WhiteBreadcrumb 
          items={[
            { label: 'ホーム', path: '/' },
            { label: '国別記事', path: '' },
            { label: decodedCountryName, path: `/country/${countryName}` }
          ]} 
        />
        
        <CountryHeader>
          <CountryTitleWrapper>
            <SectionTitle>{decodedCountryName}</SectionTitle>
          </CountryTitleWrapper>
        </CountryHeader>
        
        {filteredArticles.length > 0 ? (
          <ArticlesList>
            {filteredArticles.map((article: Article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </ArticlesList>
        ) : (
          <NoArticles>
            {decodedCountryName}に関する記事はまだありません。
          </NoArticles>
        )}
        
        <HomeButton to="/" onClick={() => window.scrollTo(0, 0)}>ホームへ戻る</HomeButton>
      </CountryContainer>
    </PageWrapper>
  );
};

export default CountryArticlesPage; 